import { ConnectionStatus, Network } from '@capacitor/network';
import { environment } from 'src/environments/environment';

const SUCCESS_TTL_MS = 15000;
const FAILURE_TTL_MS = 1000;

let cached: { at: number; online: boolean } | null = null;
let inflight: Promise<boolean> | null = null;
let installed = false;

export function resetDeviceOnlineCacheForTests(): void {
  cached = null;
  inflight = null;
  installed = false;
}

/** Plugin `connected` or the browser online flag is enough. A false reading still needs a probe. */
export function pluginOrBrowserSaysOnline(
  reportedConnected: boolean,
  navigatorOnLine: boolean | undefined,
): boolean {
  return reportedConnected === true || navigatorOnLine === true;
}

export async function probeDeviceOnline(url: string, timeoutMs = 4000): Promise<boolean> {
  if (typeof fetch !== 'function' || !url) return false;
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    await fetch(url, { method: 'GET', mode: 'no-cors', cache: 'no-store', signal: ctrl.signal });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(id);
  }
}

/**
 * Desktop webviews (including the embedded browser) often leave `navigator.onLine` false
 * while requests succeed. Confirm a negative plugin reading with one reachability probe.
 */
export async function resolveDeviceOnline(
  reportedConnected: boolean,
  probe: () => Promise<boolean>,
  navigatorOnLine?: boolean,
): Promise<boolean> {
  if (pluginOrBrowserSaysOnline(reportedConnected, navigatorOnLine)) {
    cached = { at: Date.now(), online: true };
    return true;
  }
  const now = Date.now();
  if (cached) {
    const ttl = cached.online ? SUCCESS_TTL_MS : FAILURE_TTL_MS;
    if (now - cached.at < ttl) return cached.online;
  }
  if (!inflight) {
    inflight = probe()
      .then((online) => {
        cached = { at: Date.now(), online };
        return online;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

/** Correct `Network.getStatus()` for every caller. Idempotent. */
export function installDeviceOnlineCorrection(): void {
  if (installed) return;
  installed = true;
  const original = Network.getStatus.bind(Network);
  Network.getStatus = async (): Promise<ConnectionStatus> => {
    const status = await original();
    const navigatorOnLine = typeof navigator !== 'undefined' ? navigator.onLine : undefined;
    const connected = await resolveDeviceOnline(
      !!status.connected,
      () => probeDeviceOnline(environment.appEnvironmentBaseUrl),
      navigatorOnLine,
    );
    return {
      ...status,
      connected,
      connectionType: connected
        ? status.connectionType === 'none'
          ? 'unknown'
          : status.connectionType
        : 'none',
    };
  };
}
